import type { ParseKeys } from "i18next";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomerPageLayout from "../../customer-page-layout/CustomerPageLayout";
import styles from "./filter-page.module.scss";
import { CloseIcon, InfoIcon } from "../../../../assets/icons";
import {
  TransactionFilters,
  DEFAULT_FILTERS,
  CATEGORY_OPTIONS,
  STATUS_OPTIONS,
  setPendingFilters,
  consumePendingFilters,
} from "./history.filters";
import PeriodFilter from "../../components/period-filter/PeriodFilter";
import Button from "../../../../common/ekedc-components/button/Button";

const InfoTooltip = ({ text }: { text: ParseKeys | undefined }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.infoTooltipWrapper}>
      <InfoIcon className={styles.sectionInfoIcon} />
      <div className={styles.tooltip}>{text && t(text)}</div>
    </div>
  );
};

const TOOLTIP_TEXT: { key: string; text: ParseKeys }[] = [
  {
    key: "category",
    text: 'history.transactionCategoryDescription',
  },
  {
    key: "status",
    text: 'history.web.statusTooltip',
  },
  {
    key: "period",
    text: 'history.web.periodTooltip',
  },
];

type FilterOption = { label: string; value: number | null };

interface CustomerTransactionFilterPageProps {
  categoryOptions?: ReadonlyArray<FilterOption>;
  categoryTitle?: string;
  categoryTitleKey?: ParseKeys;
  showCategory?: boolean;
  showStatus?: boolean;
  showPeriod?: boolean;
}

export const CustomerTransactionFilterPage = ({
  categoryOptions = CATEGORY_OPTIONS,
  categoryTitle,
  categoryTitleKey,
  showCategory = true,
  showStatus = true,
  showPeriod = true,
}: CustomerTransactionFilterPageProps = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const initial = consumePendingFilters() ?? DEFAULT_FILTERS;
  const [filters, setFilters] = useState<TransactionFilters>(initial);

  const handleApply = () => {
    setPendingFilters(filters);
    navigate(-1);
  };

  const periodTooltipKey = TOOLTIP_TEXT.find((x) => x.key === "period")?.text;

  return (
    <CustomerPageLayout title={t("history.filter")} icon={<CloseIcon/>} showBackButton containerClass={styles.filterContainerPage} contentContainerClass={styles.contentFilter}>
      <div className={styles.view}>
        <div className={styles.body}>
          {showCategory && (
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionTitle}>{categoryTitle ?? t(categoryTitleKey ?? "history.category")}</span>
              <InfoTooltip text={TOOLTIP_TEXT.find((x) => x.key === "category")?.text} />
            </div>
            <div className={styles.chips}>
              {categoryOptions.map((opt) => (
                <button
                  key={String(opt.value)}
                  className={`${styles.chip} ${filters.category === opt.value ? styles.chipActive : ""}`}
                  onClick={() => setFilters((prev) => ({ ...prev, category: opt.value }))}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </section>
          )}

          {showStatus && (
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionTitle}>{t("common.labels.status")}</span>
              <InfoTooltip text={TOOLTIP_TEXT.find((x) => x.key === "status")?.text} />
            </div>
            <div className={styles.chips}>
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={String(opt.value)}
                  className={`${styles.chip} ${filters.status === opt.value ? styles.chipActive : ""}`}
                  onClick={() => setFilters((prev) => ({ ...prev, status: opt.value }))}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </section>
          )}

          {showPeriod && (
          <PeriodFilter
            tooltipText={periodTooltipKey ? t(periodTooltipKey) : undefined}
            value={{ period: filters.period, startDate: filters.startDate, endDate: filters.endDate }}
            onChange={(next) => setFilters((prev) => ({ ...prev, ...next }))}
          />
          )}
        </div>

        <div className={styles.footer}>
          <Button text={t("history.clearAll")} variant="secondary" onClick={() => setFilters(DEFAULT_FILTERS)} />
          <Button text={t("history.applyFilter")} variant="primary" onClick={handleApply} />
        </div>
      </div>
    </CustomerPageLayout>
  );
};
